import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import {
    Avatar,
    Drawer,
    Flex,
    HorizontalDivider,
    IconLogout,
    NavLink,
    SideNavLink,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { useSidebarContext } from '../../contexts/SidebarProvider';
import { usePatientContext } from '../../hooks/use-patient-context';
import { getAbsoluteRoute } from '../../util';
import { AppVersion } from '../sidebar/AppVersion';
import { GettingStartedSection } from '../sidebar/sections/GettingStartedSection';
import { HelpAndSettingsSection } from '../sidebar/sections/HelpAndSettingsSection';
import { MyAccountSection } from '../sidebar/sections/MyAccountSection';
import { useMaintenanceStatus } from '../unauth-home/hooks/use-maintenance-status';
import { ConditionalComponent } from '../utility/ConditionalComponent';

export const AppSidebar = () => {
    const { close, isOpen } = useSidebarContext();
    const { disableAuthFlows, isAuthenticated, signIn } = useAuthContext();
    const { t } = useTranslation();
    const location = useLocation();
    const { patient } = usePatientContext();
    const { isMaintenanceActive } = useMaintenanceStatus();

    const login = useCallback(
        () => signIn(location.pathname),
        [signIn, location.pathname]
    );

    useEffect(() => {
        /*
         * If the route changes, we should ensure that the sidebar is closed if
         * it isn't already. Note that there is not a check for `isOpen` here
         * due to conflicts that happen when the user tries to deliberately
         * open the sidebar.
         *
         * However, since we only trigger a close when the location changes, and
         * users aren't in the habit of navigating around in rapid succession,
         * there's no real performance issue calling `close` even if the sidebar
         * is already closed.
         */
        close();
    }, [location.pathname, location.key, close]);

    const drawerHeader = (
        // oxlint-disable-next-line jsx-a11y/aria-role -- tracked in docs/oxlint-violations.md
        <Flex gap="xs" role="header">
            <Avatar size="xl" initials={patient?.initials} />
            <ConditionalComponent condition={isAuthenticated}>
                <NavLink
                    href={getAbsoluteRoute('profile.root')}
                    label={patient?.fullName}
                    variant="subtle"
                />
            </ConditionalComponent>
            <ConditionalComponent condition={!isAuthenticated}>
                <NavLink
                    disabled={isMaintenanceActive || disableAuthFlows}
                    onClick={login}
                    label={t('sidebar.button.signIn')}
                    variant="subtle"
                />
            </ConditionalComponent>
        </Flex>
    );

    return (
        <Drawer opened={isOpen} onClose={close} header={drawerHeader}>
            <Stack mih="100%" gap="xs" justify="space-between">
                <Stack gap="xs" flex={1}>
                    <GettingStartedSection />
                    <MyAccountSection />
                    <HelpAndSettingsSection />
                    <ConditionalComponent condition={isAuthenticated}>
                        <HorizontalDivider variant="middle" />
                        <SideNavLink
                            label={t('sidebar.button.signOut')}
                            Icon={IconLogout}
                            href={getAbsoluteRoute('logout')}
                        />
                    </ConditionalComponent>
                </Stack>
                <AppVersion />
            </Stack>
        </Drawer>
    );
};
